import { css } from '@emotion/react';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo,
} from 'react';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { ImageGalleryComponent } from '~/_shared/components/imageGallery/imageGallery.component';
import { isSafari } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type { useLocationImageGallery } from '~/customizeLocationPanel/useLocationImageGallery';
import type { Place } from '~/places/places.types';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { getPlacesToolCircleColors } from '../../mapTools/placesTool/placesTool.helpers';
import { placeCircleStyle } from '../../mapTools/placesTool/placesTool.styles';
import { type SidebarRoundMenuButtonProps } from '../roundButtonMenu/sidebarRoundButtonMenu.types';
import { SidebarContentContainer } from '../sidebarContent/sidebarContent.container';
import { SidebarTitleComponent } from '../sidebarTitle/sidebarTitle.component';

const descriptionWrapperStyle = css({
  padding: '22px 0',
});

const contentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
});

const IMAGE_GALLERY_HEIGHT = 225;
const imageGalleryStyle = css({
  height: IMAGE_GALLERY_HEIGHT,
});

type PlaceDetailsComponentProps = Readonly<{
  placeSearch: PlacesSearch;
  placeIndex: number;
  place: Place;
  gallery?: ReturnType<typeof useLocationImageGallery>;
  onClose: () => void;
  onExpandClick?: () => void;
  onRemoveFromSearchResults?: () => void;
}>;

export const PlaceDetailsComponent: FC<PlaceDetailsComponentProps> = ({
  placeSearch,
  placeIndex,
  place,
  gallery,
  onClose,
  onExpandClick,
  onRemoveFromSearchResults,
}) => {
  const [t] = useTranslation();

  const rightMenuButtons = useMemo(() => {
    const buttons: SidebarRoundMenuButtonProps<string>[] = [];
    if (notNullsy(onRemoveFromSearchResults)) {
      buttons.push({
        id: 'removePlaceFromSearchResultsButton',
        buttonName: 'remove-from-search-results',
        buttonStyle: RoundButtonStyle.Danger,
        icon: faTrash,
        onClick: onRemoveFromSearchResults,
        tooltipLabel: t('placesTool.place.removeFromResults'),
      });
    }
    return buttons;
  }, [onRemoveFromSearchResults, t]);

  return (
    <SidebarContentContainer
      description={(
        <SidebarTitleComponent
          onXClick={onClose}
          sidebarTitle={{
            header: place.name,
            description: [place.address],
          }}
          containerStyle={descriptionWrapperStyle}
          indicator={(
            <div
              css={placeCircleStyle(getPlacesToolCircleColors(placeSearch.color))}
            >
              {placeIndex + 1}
            </div>
          )}
        />
      )}
      menuProps={rightMenuButtons.length > 0 ? {
        selectedButton: null,
        roundButtonRightDetails: rightMenuButtons,
      } : undefined}
      isLoading={false}
      body={(
        <div css={contentStyle}>
          <div /> {/* additional info will go into this div in phase 2. Keeping here so styles don't need to be reworked */}
          <div>
            {gallery && (
              <ImageGalleryComponent
                items={gallery.galleryItems}
                renderItem={gallery.renderGalleryItem}
                css={imageGalleryStyle}
                onImageSelect={noop}
                onExpandClick={onExpandClick}
                triggerTooltip={t('Launch Street View')}
                allowOverflow={isSafari()}
              />
            )}
          </div>
        </div>
      )}
    />
  );
};
